import { Link, useParams, Navigate } from "react-router-dom";
import { Calendar, User, ArrowRight, ArrowLeft, Tag } from "lucide-react";
import Footer from "@/components/Footer";
import BackToTop from "@/components/BackToTop";
import PublicHeader from "@/components/PublicHeader";
import { useMeta } from "@/lib/useMeta";
import { COMPANY } from "@/lib/company";

const GOLD = "#C9A84C";

export const POSTS = [
  {
    slug: "why-percentage-progress-lies",
    title: "Why percentage progress lies — and what to measure instead",
    excerpt:
      "Ask five engineers for progress on the same front and you will get five numbers. The problem is not honesty; it is that percentage is a conclusion, not a measurement.",
    date: "2026-06-18",
    author: "Plan-Sarthi Editorial",
    tag: "Project Controls",
    read: "6 min",
    body: [
      ["The 90% syndrome", "Every planner knows the front that reached 90% in month three and stayed there for a year. It happens because a percentage is a judgement, and judgement drifts towards optimism when a review is coming. Physical quantity does not drift. Either 412 cubic metres were poured or they were not."],
      ["Measure the denominator first", "A percentage is only as good as the scope it divides by. If your WBS says \"Civil works — 1 No\", nothing downstream can be right. Break scope into the units your contract already uses — Cu.M of concrete, MT of structural steel, Dia-Inch of piping, RMT of cable, Sq.M of shuttering — and the percentage becomes an output rather than an opinion."],
      ["Then measure the rate, not just the total", "Cumulative progress hides the trend. A front at 60% that is doing 40 Cu.M a day will finish; the same front doing 8 Cu.M a day will not. Compare the trailing 21-day rate against the rate the balance quantity demands, and you have a forecast finish date instead of a feeling."],
      ["What this changes at the review", "The conversation stops being \"are you sure it's 72%\" and becomes \"you need 34 Cu.M a day and you are doing 19 — what do you need\". That is a solvable question, and it is the only one that matters four months before a milestone."],
    ],
  },
  {
    slug: "earned-value-for-indian-contractors",
    title: "Earned value without the jargon: a practical guide for Indian contractors",
    excerpt:
      "CPI, SPI, EAC and VAC sound like consultant vocabulary. They are actually four simple questions about whether you are going to lose money on this package.",
    date: "2026-06-10",
    author: "Plan-Sarthi Editorial",
    tag: "Costing & EVM",
    read: "8 min",
    body: [
      ["Four numbers, four questions", "Planned value asks what you promised to have done by today. Earned value asks what you have actually done, priced at contract rates. Actual cost asks what it cost you to do it. Everything else — CPI, SPI, EAC, VAC — is arithmetic on those three."],
      ["Why physical progress must drive it", "If earned value is computed from invoices or from a percentage someone typed in, the indices are decorative. It has to come from validated executed quantity — the DPR your site engineer signed and your planner cleared — otherwise CPI simply reflects your billing cycle."],
      ["The two leaks nobody prices", "Idle manpower and hindrance days are real money and almost never appear in a monthly cost report. Fifteen workers idle for six hours because a crane did not arrive is a number you can compute and, more importantly, a number you can claim. Capture it the day it happens, not in a reconstruction a year later."],
      ["Reading the indices honestly", "A CPI of 0.94 on a ₹180 crore package is not a rounding error; it is roughly ₹10 crore of margin at completion. The value of EVM is not the ratio, it is that the ratio moves months before your P&L does."],
    ],
  },
  {
    slug: "dpr-discipline-that-actually-sticks",
    title: "DPR discipline that actually sticks on site",
    excerpt:
      "Most daily progress reporting dies within a month, not because engineers are lazy, but because the form takes twelve minutes and gives them nothing back.",
    date: "2026-05-28",
    author: "Plan-Sarthi Editorial",
    tag: "Field Execution",
    read: "5 min",
    body: [
      ["Make it shorter than a WhatsApp message", "A DPR that asks for quantity, shift, manpower split, idle hours and weather against a pre-selected activity takes under a minute on a phone. Anything longer competes with the actual work and loses."],
      ["Assume there is no network", "Half of India's project sites have no usable signal where the work is happening. If the form needs connectivity, engineers write on paper and enter it three days later from memory — which is how quantities get rounded and dates get vague. Store the entry on the device and sync it later."],
      ["Give something back immediately", "The moment a DPR is validated, the engineer should see the balance quantity drop, the S-curve move and the forecast date shift. Reporting that visibly changes something gets done. Reporting that disappears into a mailbox does not."],
      ["Validate, don't just collect", "An unvalidated DPR should not count towards progress or earned value. One authorised clearance step is the difference between a data set you can defend in a claim and a pile of numbers."],
    ],
  },
  {
    slug: "hindrance-register-that-wins-claims",
    title: "The hindrance register that wins extension-of-time claims",
    excerpt:
      "Most delay claims fail on evidence, not on merit. The difference is whether the delay was recorded on the day it happened or reconstructed eighteen months later.",
    date: "2026-05-14",
    author: "Plan-Sarthi Editorial",
    tag: "Claims & Contracts",
    read: "7 min",
    body: [
      ["Record it the day it happens", "A hindrance logged on the date, against the specific activity, with the crew and plant idled, is a contemporaneous record. The same facts assembled from memory before an arbitration are an assertion. Consultants can tell the difference in a minute."],
      ["Attribute it honestly", "Client-side, contractor-side or neutral — write down which. A register where every delay is the client's fault destroys its own credibility, and with it the entries that were genuinely theirs."],
      ["Price it and link it to float", "A delay only entitles you to time if it consumed float on the critical path. Log the hindrance against the activity, let the CPM engine show whether that activity was driving completion, and attach the idle-cost computation. Time and money in one record."],
      ["Notice periods are unforgiving", "Most Indian EPC contracts require notice within a fixed window. A register that is up to date makes notices routine; one that is three months behind quietly forfeits the entitlement."],
    ],
  },
  {
    slug: "ai-in-project-controls-what-actually-works",
    title: "AI in project controls: what actually works, and what is theatre",
    excerpt:
      "Forecasting a slipping work front is a genuinely useful application of machine intelligence. Asking a chatbot to write your monthly report is not.",
    date: "2026-04-30",
    author: "Plan-Sarthi Editorial",
    tag: "AI & Analytics",
    read: "6 min",
    body: [
      ["What works: extraction and extrapolation", "Reading a 4,000-line BOQ out of a badly formatted PDF and normalising its units is drudgery a machine does well — provided a planner reviews it line by line before it becomes live scope. Extrapolating run-rates into a forecast finish date is arithmetic the model just explains well."],
      ["What is theatre: confident narratives with no data behind them", "A model will happily write a polished paragraph about a project it knows nothing about. If a risk narrative is not grounded in validated quantities and dated progress, it is prose, not analysis — and it is dangerous precisely because it reads well."],
      ["Confidence must be visible", "Two weeks of DPR history cannot forecast a two-year package. Any honest system states how much data its forecast rests on and lowers its own confidence when the history is thin."],
      ["Keep the human sign-off", "Nothing from a model should reach a client, a lender or an auditor without a qualified engineer's review. Used that way, AI removes hours of clerical work. Used the other way, it manufactures liability."],
    ],
  },
];

const fmt = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" });

export default function Blog() {
  useMeta({
    title: "Plan-Sarthi Blog — EPC Project Controls, Planning & Claims Insights",
    description:
      "Practical writing on EPC project controls for Indian contractors: measuring physical progress, earned value, DPR discipline, hindrance registers and AI in planning.",
    path: "/blog",
  });

  return (
    <div className="min-h-screen bg-[#0A1628] text-white flex flex-col" data-testid="blog-page">
      <PublicHeader />
      <main className="flex-1 max-w-[1000px] mx-auto px-5 sm:px-8 py-14 w-full">
        <span className="text-[11px] uppercase tracking-[0.18em]" style={{ color: GOLD }}>Blog</span>
        <h1 className="font-display text-3xl sm:text-4xl font-bold leading-tight mt-4" data-testid="page-title">
          Field notes on project controls
        </h1>
        <p className="text-base text-white/70 mt-5 leading-relaxed max-w-[720px]">
          Written for planning engineers, project managers and contract teams executing heavy industrial work in India.
          No vendor fluff — just how to measure execution properly and defend it commercially.
        </p>

        <div className="grid sm:grid-cols-2 gap-3 mt-10" data-testid="blog-list">
          {POSTS.map((p) => (
            <Link
              key={p.slug}
              to={`/blog/${p.slug}`}
              className="border border-white/10 bg-[#112240] rounded-lg p-5 hover:border-[#C9A84C]/45 transition-colors duration-200 flex flex-col"
              data-testid={`blog-card-${p.slug}`}
            >
              <span className="inline-flex items-center gap-1.5 text-[11px]" style={{ color: GOLD }}>
                <Tag className="w-3 h-3" /> {p.tag}
              </span>
              <h2 className="font-semibold text-[15.5px] mt-2.5 leading-snug">{p.title}</h2>
              <p className="text-[13px] text-white/60 mt-2.5 leading-relaxed flex-1">{p.excerpt}</p>
              <div className="flex items-center gap-4 mt-4 text-[11px] text-white/45">
                <span className="flex items-center gap-1.5"><Calendar className="w-3 h-3" /> {fmt(p.date)}</span>
                <span>{p.read} read</span>
              </div>
            </Link>
          ))}
        </div>

        <div className="border rounded-lg p-6 mt-10" style={{ borderColor: "rgba(201,168,76,0.35)", background: "rgba(201,168,76,0.07)" }}>
          <h2 className="text-base md:text-lg font-semibold">Want this applied to your own package?</h2>
          <p className="text-[13px] text-white/65 mt-2 leading-relaxed">
            Start the 14-day free trial, or send us a live BOQ and we will show you the same principles on your data.
          </p>
          <div className="flex flex-wrap gap-3 mt-5">
            <Link to="/signup" className="inline-flex items-center h-10 px-5 rounded-lg text-[14px] font-semibold text-white" style={{ backgroundColor: GOLD }} data-testid="blog-trial-btn">
              Start Free Trial
            </Link>
            <Link to="/demo" className="inline-flex items-center h-10 px-5 rounded-lg border border-white/35 text-[14px] text-white hover:bg-white/10 transition-colors duration-200" data-testid="blog-demo-btn">
              Book a live demo
            </Link>
          </div>
        </div>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const post = POSTS.find((p) => p.slug === slug);
  useMeta({
    title: post ? `${post.title} | Plan-Sarthi Blog` : "Plan-Sarthi Blog",
    description: post?.excerpt,
    path: `/blog/${slug}`,
  });
  if (!post) return <Navigate to="/blog" replace />;

  const others = POSTS.filter((p) => p.slug !== slug).slice(0, 2);

  return (
    <div className="min-h-screen bg-[#0A1628] text-white flex flex-col" data-testid={`blog-post-${slug}`}>
      <PublicHeader />
      <main className="flex-1 max-w-[820px] mx-auto px-5 sm:px-8 py-14 w-full">
        <Link to="/blog" className="inline-flex items-center gap-1.5 text-[12.5px] text-white/60 hover:text-white" data-testid="blog-back-link">
          <ArrowLeft className="w-3.5 h-3.5" /> All posts
        </Link>
        <span className="inline-flex items-center gap-1.5 text-[11px] mt-6" style={{ color: GOLD }}>
          <Tag className="w-3 h-3" /> {post.tag}
        </span>
        <h1 className="font-display text-3xl sm:text-4xl font-bold leading-[1.12] mt-3" data-testid="post-title">{post.title}</h1>
        <div className="flex flex-wrap items-center gap-4 mt-5 text-[11.5px] text-white/45">
          <span className="flex items-center gap-1.5"><User className="w-3.5 h-3.5" /> {post.author}</span>
          <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> {fmt(post.date)}</span>
          <span>{post.read} read</span>
        </div>
        <p className="text-base sm:text-lg text-white/80 mt-7 leading-relaxed">{post.excerpt}</p>

        <div className="mt-10 space-y-8">
          {post.body.map(([heading, text]) => (
            <section key={heading}>
              <h2 className="text-base md:text-lg font-semibold" style={{ color: GOLD }}>{heading}</h2>
              <p className="text-[14px] text-white/70 mt-3 leading-[1.75]">{text}</p>
            </section>
          ))}
        </div>

        <div className="border rounded-lg p-6 mt-12" style={{ borderColor: "rgba(201,168,76,0.35)", background: "rgba(201,168,76,0.07)" }}>
          <p className="text-[14px] text-white/80 leading-relaxed">
            Plan-Sarthi puts these practices into one workspace — validated DPRs driving the S-curve, critical path,
            earned value and forecast automatically.
          </p>
          <Link to="/signup" className="inline-flex items-center gap-2 mt-5 h-10 px-5 rounded-lg text-[14px] font-semibold text-white" style={{ backgroundColor: GOLD }} data-testid="post-trial-btn">
            Start the 14-day free trial <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <h2 className="text-base md:text-lg font-semibold uppercase tracking-[0.14em] mt-14" style={{ color: GOLD }}>Read next</h2>
        <div className="grid sm:grid-cols-2 gap-3 mt-5">
          {others.map((o) => (
            <Link key={o.slug} to={`/blog/${o.slug}`} className="border border-white/10 bg-[#112240] rounded-lg p-4 hover:border-[#C9A84C]/45 transition-colors duration-200" data-testid={`read-next-${o.slug}`}>
              <div className="font-medium text-[14px] leading-snug">{o.title}</div>
              <div className="text-[11.5px] text-white/50 mt-2">{o.read} read · {o.tag}</div>
            </Link>
          ))}
        </div>
        <p className="text-[12px] text-white/40 mt-10">
          Questions or a topic you want covered? Write to{" "}
          <a href={`mailto:${COMPANY.email}`} className="hover:underline" style={{ color: GOLD }}>{COMPANY.email}</a>.
        </p>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
}
